import React, { useState, useEffect, useRef } from "react";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import axios from "axios";

export default function Settings() {
    const toast = useRef(null);

    const [settings, setSettings] = useState([]);
    const [platforms, setPlatforms] = useState([]);
    const [visible, setVisible] = useState(false);
    const [editingSetting, setEditingSetting] = useState(null);
    const [globalFilter, setGlobalFilter] = useState("");

    const [formData, setFormData] = useState({
        id: null,
        platformid: "",
        settingName: "",
        settingType: "",
        settingTitle: "",
    });

    // Fetch all settings
    const fetchSettings = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/settings');
            setSettings(res.data || []);
        } catch (err) {
            console.error(err);
        }
    };

    // Fetch all platforms
    const fetchPlatforms = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/platform');
            // normalize value to string
            setPlatforms((res.data || []).map((p) => ({
                label: p.platform_name,
                value: String(p.platform_id),
            })));
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchSettings();
        fetchPlatforms();
    }, []);

    // Handle input changes
    const onInputChange = (e, name) => {
        setFormData({ ...formData, [name]: e.target.value });
    };

    // Save or Update Setting
    const saveSetting = async () => {
        try {
            const url = editingSetting ? 'https://treatway.co.in/Counter-Backend/api/server_masters/save/settings' : 'https://treatway.co.in/Counter-Backend/api/server_masters/save/settings';

            await axios.post(url, JSON.stringify(formData), {
                headers: { "Content-Type": "application/json" },
            });

            toast.current.show({
                severity: "success",
                summary: "Success",
                detail: editingSetting ? "Setting Updated" : "Setting Added"
            });

            setVisible(false);
            setEditingSetting(null);
            setFormData({ id: null, platformid: "", settingName: "", settingType: "", settingTitle: "" });
            fetchSettings(); // refresh table
        } catch (err) {
            console.error(err);
            toast.current.show({ severity: "error", summary: "Error", detail: "Something went wrong" });
        }
    };

    // Open dialog for edit
    const editSetting = async (rowData) => {
        try {
            const res = await axios.get(`https://treatway.co.in/Counter-Backend/api/server_masters/get_setting_by_id/${rowData.setting_id}`);

            const data = res.data;

            setEditingSetting(true);
            setFormData({
                id: data.setting_id,
                platformid: data.platform_id ? String(data.platform_id) : "",
                settingName: data.setting_name,
                settingType: data.setting_type,
                settingTitle: data.setting_title,
            });
            setVisible(true);
        } catch (err) {
            console.error(err);
            toast.current.show({ severity: "error", summary: "Error", detail: "Failed to load setting details" });
        }
    };


    // Dialog footer
    const dialogFooter = (
        <div>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setVisible(false)} />
            <Button label="Save" icon="pi pi-check" onClick={saveSetting} />
        </div>
    );

    return (
        <>
        <Toast ref={toast} />
        <h2 className="text-xl font-bold mb-4">Settings</h2>
        <div className="card">
            <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
                <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                    <Button
                        label="Add Setting"
                        icon="pi pi-plus"
                        onClick={() => {
                            setVisible(true);
                            setEditingSetting(null);
                            setFormData({ id: null, platformid: "", settingName: "", settingType: "", settingTitle: "" });
                        }}
                    />
                </div>
                <div className="flex items-center ml-auto min-w-[250px] justify-end w-full sm:w-auto">
                    <span className="p-input-icon-left w-full">
                        <i className="pi pi-search"></i>
                        <InputText
                            value={globalFilter}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            placeholder="Search..."
                            className="p-inputtext-sm w-full"
                        />
                    </span>
                </div>
            </div>

            <DataTable value={settings} 
                showGridlines
                paginator
                rows={25}
                rowsPerPageOptions={[10, 25, 50]}
                paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                currentPageReportTemplate="{first} to {last} of {totalRecords}"
                tableStyle={{ minWidth: '50rem' }}
                className="p-datatable-sm"
                emptyMessage="No settings found." globalFilter={globalFilter}
            >
                <Column field="setting_name" header="Name" />
                <Column field="setting_type" header="Type" />
                <Column field="setting_title" header="Title" />
                <Column
                    header="Actions"
                    body={(rowData) => (
                        <Button
                            icon="pi pi-pencil"
                            className="p-button-rounded p-button-warning mr-2"
                            onClick={() => editSetting(rowData)}
                        />
                    )}
                />
            </DataTable>

            <Dialog
                header={editingSetting ? "Edit Setting" : "Add Setting"}
                visible={visible}
                style={{ width: "400px" }}
                footer={dialogFooter}
                onHide={() => setVisible(false)}
            >
                <div className="field">
                    <label htmlFor="platformid">Platform</label>
                    <Dropdown
                        id="platformid"
                        value={formData.platformid}
                        options={platforms}
                        onChange={(e) => setFormData({ ...formData, platformid: e.value })}
                        placeholder="Select a Platform"
                        className="w-full"
                    />
                </div>

                <div className="field">
                    <label htmlFor="settingName">Setting Name</label>
                    <InputText id="settingName" value={formData.settingName} onChange={(e) => onInputChange(e, "settingName")} className="w-full" />
                </div>

                <div className="field">
                    <label htmlFor="settingType">Setting Type</label>
                    <InputText id="settingType" value={formData.settingType} onChange={(e) => onInputChange(e, "settingType")} className="w-full" />
                </div>

                <div className="field">
                    <label htmlFor="settingTitle">Setting Title</label>
                    <InputText id="settingTitle" value={formData.settingTitle} onChange={(e) => onInputChange(e, "settingTitle")} className="w-full" />
                </div>
            </Dialog>
        </div>
        </>
    );
}
